import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { convertKeysToCamelCase, convertSnakeToCamelCase } from '@shared/utils';
import { FormGroup } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  convertKeysToCamelCase(obj: any): any {
    return convertKeysToCamelCase(obj);
  }

  getGridPageForRequest(skip: number, pageSize: number): number {
    return skip === 0 ? 1 : skip / pageSize + 1;
  }

  capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  calculateFileSize(fileSize: number): string {
    // Check for MacOS when calculating file size
    const macRegex = /Mac|iPhone|iPod|iPad/i;
    const isMac =
      macRegex.test(navigator.platform) ||
      // @ts-expect-error - userAgentData replaces the deprecated navigator.platform
      macRegex.test(navigator.userAgentData?.platform);

    const denominator = isMac ? 1000000 : 1048576;
    return (fileSize / denominator).toFixed(1) + ' MB';
  }

  trimFormTextContainer(form: FormGroup<any>, controlName: string) {
    form.get(controlName)?.setValue(form.get(controlName)?.value.trim());
  }

  camelToSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }

  convertKeysToSnakeCase(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => this.convertKeysToSnakeCase(item));
    }
    return Object.keys(obj).reduce((acc: any, key) => {
      const snakeKey = this.camelToSnakeCase(key);
      acc[snakeKey] = this.convertKeysToSnakeCase(obj[key]);
      return acc;
    }, {});
  }

  convertSnakeToCamelCase(str: string) {
    return convertSnakeToCamelCase(str);
  }

  stripHtml(html: string): string {
    let stripped = html.replace(/<[^>]*>/g, '');
    stripped = stripped.replace(/&[^;]+;/g, '');
    return stripped.trim();
  }

  getEasternTime(date: Date): string {
    // output: 09-27-2024_11.45.54 ET
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };

    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(date);

    const getPart = (type: string) => parts.find((p) => p.type === type)?.value;

    const month = getPart('month');
    const day = getPart('day');
    const year = getPart('year');
    const hour = getPart('hour');
    const minute = getPart('minute');
    const second = getPart('second');

    return `${month}-${day}-${year} ${hour}.${minute}.${second} ET`;
  }

  sortByDate = (a: any, b: any, order: 'asc' | 'desc' = 'desc') => {
    const getDateAsIsoString = (date: string) => {
      return new Date(date).toISOString();
    };

    const dateA = a?.document?.created_dt
      ? getDateAsIsoString(a.document.created_dt)
      : a?.created_dt
        ? getDateAsIsoString(a.created_dt)
        : 0;
    const dateB = b?.document?.created_dt
      ? getDateAsIsoString(b.document.created_dt)
      : b?.created_dt
        ? getDateAsIsoString(b.created_dt)
        : 0;

    return order === 'asc' ? +dateA - +dateB : +dateB - +dateA;
  };

  getUserTimezoneOffset(): string {
    const offsetMinutes = new Date().getTimezoneOffset();
    const sign = offsetMinutes <= 0 ? '+' : '-';
    const absOffsetMinutes = Math.abs(offsetMinutes);

    const hours = Math.floor(absOffsetMinutes / 60);
    const minutes = absOffsetMinutes % 60;

    const formattedOffset = `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    return formattedOffset;
  }

  resetError(form: FormGroup): void {
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      if (control) {
        control.setErrors(null);
      }
    });
  }
}

/**
 * getDataFromObject
 * Finds a value using dot.notation in an object
 * @param object {any} to search
 * @param path {string} of value to find
 * @returns {any}
 */
export function getDataFromObject(object: any, path: string): any {
  if (!object || !path) {
    return undefined;
  }

  const pathArray = path.split('.');
  let current = object;

  for (const key of pathArray) {
    if (!current[key]) {
      return undefined;
    }
    current = current[key];
  }

  return current;
}

/**
 * getTimeZoneAbbreviation
 * Returns the shortened version of the given long-from timeZone
 * @param timeZone {string} time zone like America/New_York
 * @returns {string}
 */
export function getTimeZoneAbbreviation(timeZone: string): string | null {
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = {
    timeZone,
    timeZoneName: 'short',
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const formattedDate = formatter.formatToParts(date);

  // Find the timeZoneName part
  const timeZonePart = formattedDate.find(
    (part) => part.type === 'timeZoneName',
  );
  return timeZonePart ? timeZonePart.value : null;
}

/**
 * getTimeStamp
 * Returns a formatted timestamp with the user's timezone
 * @returns {string} formatted timestamp
 */
export function getTimeStamp(format?: string): string {
  const userTimezoneLabel = getTimeZoneAbbreviation(
    getUserTimezoneIdentifier(),
  );
  const time = moment(new Date()).format(format ?? 'DD-MMM-YYYY HH.mm.ss');
  return `${time} ${userTimezoneLabel}`;
}

export function getFileNameTimeStamp(format?: string): string {
  const time = moment()
    .tz('America/New_York')
    .format(format ?? 'DD-MMM-YYYY HH.mm.ss');
  return `${time} EST`;
}

export function getUTCTimeStamp(format?: string): string {
  const utc = moment.utc().format(format ?? 'DD-MMM-YYYY HH_mm_ss');
  return `${utc} UTC`;
}

export function getUserTimezoneIdentifier(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
